/**
 * FP 설비 정지 기간(비조업 설정) 위젯 — [D-120] 진행률 표 화면 행 8.
 *
 * ★ 무엇을 하나
 *   카탈로그 레버 `resource_downtime`(period_list · CALENDAR · scopes [resource])을 시작일~종료일 행으로
 *   편집한다. 공용 편집기(scenarioParamDefsFields)는 period_list 를 맨 텍스트 칸으로 떨어뜨리므로 그쪽에는
 *   정의를 넘기지 않고(`useFpDowntimeSlot` 의 shared 사본) 이 위젯이 따로 그린다. 정지할 설비는 기존
 *   「적용 대상」 블록(FpScenarioTargets)에서 고른다 — 정의가 카드 param_defs 에 남아 있어 자원 축과
 *   「대상 미지정」 경고가 그대로 선다. 공용 파일은 0줄이다.
 *
 * ★ 저장 정본 = 엔진 의미 — `[{start:"YYYY-MM-DD", end:"마지막 정지일+1"}]`
 *   엔진(FpAiScenarioParamApplier.applyCalendarSection · dateTime)은 날짜만 오면 00:00 으로 읽고
 *   `start < end` 를 요구한다 ⇒ 하루 정지는 {D, D+1}. 화면은 종료일을 **포함**으로 보여 주고 저장할 때만
 *   +1 한다(제품 비조업 설정과 같은 의미). 정본을 엔진 의미로 두면 웹 변환을 거치지 않는 경로
 *   (커스텀 저장 → 재로드 · 서버 직통)에서도 뜻이 같다. cycle 은 쓰지 않는다(비면 1회).
 *
 * ★ 겹침은 경고하지 않는다 — 비가동(D) 구간은 엔진이 합집합으로 처리한다(MP 기간 위젯과 다른 점).
 *
 * ★ 순환 import 금지 — 이 파일은 AiFpScenarioPlanning.js 를 import 하지 않는다(그 파일이 이 파일을
 *   import 한다). 계획 기간처럼 모듈 객체에 있는 값은 인자로 받는다.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { AiButton } from "@plannel/components/ai/AiButton";
import { AiDeleteButton } from "@plannel/components/ai/AiDeleteButton";
import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { PARAM_CARD_RADIUS } from "./scenarioParamUtil";

// 서버 가드(test_fp_param_catalog.py)가 이 줄을 정규식으로 읽는다 — 카탈로그 period_list 와 1:1. 형태를 바꾸지 말 것.
export const FP_DOWNTIME_KEYS = new Set(["resource_downtime"]);

const DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const pad2 = (n) => String(n).padStart(2, "0");
const isWin = (w) => !!w && typeof w === "object" && !Array.isArray(w) && ("start" in w || "end" in w);
const dayOf = (s) => {
  const v = s == null ? "" : String(s).trim();
  return DATE_RE.test(v) ? v.slice(0, 10) : "";
};

export const fpIsDowntimeDef = (d) => FP_DOWNTIME_KEYS.has(d?.param_key) && d?.type === "period_list";

export const fpIsDowntimeValue = (raw, def) =>
  def?.type === "period_list" || isWin(raw) || (Array.isArray(raw) && raw.some(isWin));

/** 배열·단일 객체 → [{start,end}] 문자열 행. 객체가 아닌 원소는 버린다. */
export function fpDowntimeRows(value) {
  const list = Array.isArray(value) ? value : (isWin(value) ? [value] : []);
  return list.filter(isWin).map((w) => ({
    start: typeof w.start === "string" ? w.start.trim().replace(" ", "T") : "",
    end:   typeof w.end === "string" ? w.end.trim().replace(" ", "T") : "",
  }));
}

/** "YYYY-MM-DD" + n일 — UTC 로 계산해 브라우저 시간대에 흔들리지 않는다. 못 읽으면 "". */
function shiftDay(day, n) {
  const d = dayOf(day);
  if (!d) return "";
  const [y, m, dd] = d.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, dd + n));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/** 마지막 정지일(포함) → 저장 end(배타 = 다음 날 0시). */
export const fpDowntimeExcl = (lastDay) => shiftDay(lastDay, 1);

/** 저장 end → 마지막 정지일(포함). 날짜만이거나 00:00 이면 −1일, 그 밖의 시각이면 그날이 마지막 날이다. */
export function fpDowntimeLastDay(end) {
  const d = dayOf(end);
  if (!d) return "";
  const time = String(end).trim().slice(10).replace(/^T/, "");
  return !time || /^00:00(:00(\.0+)?)?$/.test(time) ? shiftDay(d, -1) : d;
}

// 비교용 시각 문자열 — "D" 와 "DT00:00" 이 같은 시각이어야 길이 0 구간(엔진이 거부)을 놓치지 않는다.
const tsOf = (s) => {
  const v = String(s).trim().replace(" ", "T");
  return v.length === 10 ? `${v}T00:00:00` : (v.length === 16 ? `${v}:00` : v);
};

/** 행 판정 — "incomplete"(빈칸) | "reversed"(시작 ≥ 끝 — 엔진이 거부한다) | null */
export function fpDowntimeRowIssue(r) {
  if (!dayOf(r?.start) || !dayOf(r?.end)) return "incomplete";
  return tsOf(r.start) >= tsOf(r.end) ? "reversed" : null;
}

/** 엔진 전송값 — 완성 행만 {start,end} 2키로. 없으면 ""(호출부의 빈 값 검사가 전송에서 뺀다). */
export function fpDowntimeEngineValue(v) {
  const ok = fpDowntimeRows(v).filter((r) => !fpDowntimeRowIssue(r)).map((r) => ({ start: r.start, end: r.end }));
  return ok.length ? ok : "";
}

/** 표시 — "10-03 ~ 10-05, 10-10"(같은 날은 하나) · 빈칸 행은 "—" · 문자열은 원문 · 행이 없으면 null. */
export function fpFormatDowntime(v) {
  if (typeof v === "string") return v.trim() || null;
  const rows = fpDowntimeRows(v);
  if (!rows.length) return null;
  return rows.map((r) => {
    const s = dayOf(r.start);
    const last = fpDowntimeLastDay(r.end);
    if (!s || !last) return "—";
    return s === last ? s.slice(5) : `${s.slice(5)} ~ ${last.slice(5)}`;
  }).join(", ");
}

/** 완성 행 중 하나라도 계획 기간 [start, end](양끝 포함) 밖에 걸치면 true. 계획 기간을 모르면 false. */
export function fpDowntimeOutOfPlan(rows, plan) {
  const ps = dayOf(plan?.start);
  const pe = dayOf(plan?.end);
  if (!ps || !pe) return false;
  return (rows || []).some((r) => !fpDowntimeRowIssue(r) && (dayOf(r.start) < ps || fpDowntimeLastDay(r.end) > pe));
}

/** 현황 응답 → 계획 기간 {start,end}("YYYY-MM-DD") | null — 현황 탭 머리의 계획 기간과 같은 값이다. */
export function fpPlanPeriodOf(data) {
  const cs = data?.current_state;
  const start = dayOf(cs?.plan_start_ts);
  const end = dayOf(cs?.plan_end_ts);
  return start && end ? { start, end } : null;
}

/** param_def → 공용 편집기에 넘길 def(0~1개). 비조업이면 표시 함수(format)를 붙인 사본 — 「커스텀 시나리오로
 *  저장」 팝업은 모듈 훅 없이 def.format 을 쓴다(ScenarioSaveCustomDialog → fmtParamDisplayValue). */
export function fpDowntimeDefForEditor(def) {
  if (!fpIsDowntimeDef(def)) return [];
  return [{ ...def, current_display: null, format: fpFormatDowntime }];
}

/** JSON 복원본(redux 미러 — 함수는 사라진다)에도 같은 자리에서 format 을 되붙인다. sc 를 그대로 돌려준다.
 *  🔴 상태 갱신(onScenarioChange)으로 붙이면 안 된다 — updateScenarios 가 엔진 결과를 무효화한다. */
export function fpAttachDowntimeFormat(sc) {
  for (const d of sc?.param_defs || []) {
    if (fpIsDowntimeDef(d) && typeof d.format !== "function" && Object.isExtensible(d)) d.format = fpFormatDowntime;
  }
  return sc;
}

/** 평탄화(fpFlattenVariant.put) — 행이 있으면 목록 그대로 보존, 못 읽는 값(문자열 등)은 버리고 알린다([Q-158]). */
export function fpDowntimePut(params, key, value) {
  const rows = fpDowntimeRows(value);
  if (rows.length) { params[key] = rows; return; }
  if (Array.isArray(value) && !value.length) return;
  console.warn(`[FP] ${key}: 구간 목록으로 읽을 수 없는 값이라 버린다 —`, value);
}

/** "YYYY-MM-DD" → 로컬 자정 Date(MP 기간 위젯과 같은 방식) · 빈 값 null */
export function fpDtStrToDate(s) {
  const d = dayOf(s);
  if (!d) return null;
  const [y, m, dd] = d.split("-").map(Number);
  const dt = new Date(2000, 0, 1);
  dt.setFullYear(y, m - 1, dd);   // new Date(y,…) 는 0–99년을 19xx 로 바꾼다
  return dt;
}

/** Date → "YYYY-MM-DD"(로컬) · null → "" · Invalid Date·네 자리 미만 연도 → undefined(입력 중이다 — 쓰지 않는다).
 *  🔴 MUI X 는 연도를 칠 때마다 중간 날짜(0002-10-05)를 「유효」로 낸다 — 그대로 쓰면 칸이 지워진다. */
export function fpDtDateToStr(dt) {
  if (dt == null) return "";
  if (!(dt instanceof Date) || Number.isNaN(dt.getTime())) return undefined;
  const y = dt.getFullYear();
  if (y < 1000) return undefined;
  return `${String(y).padStart(4, "0")}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

/** FpScenarioParamFields 가 쓰는 슬롯 — 공용 편집기용 사본 · 조정 건수 래퍼 · 위젯 props.
 *  🔴 조정 건수: 공용 편집기(자식)의 effect 가 이 컴포넌트(부모)의 effect 보다 먼저 돌고, 컨테이너는
 *     **첫 보고**로 뷰(조정만/전체)를 정한다 ⇒ 비조업분은 렌더 중 ref 에 대입하고 래퍼가 호출 시점에 더한다.
 *     공용 편집기는 비조업 값만 바뀌면 다시 보고하지 않으므로(deps 가 자기 건수뿐) 여기서 다시 보고한다. */
export function useFpDowntimeSlot(scenario, rest, planPeriod) {
  const defs = scenario?.param_defs;
  const def = useMemo(() => (defs || []).find(fpIsDowntimeDef) || null, [defs]);
  const shared = useMemo(
    () => (def ? { ...scenario, param_defs: defs.filter((d) => !fpIsDowntimeDef(d)) } : scenario),
    [scenario, defs, def],
  );
  const value = def ? scenario?.params?.[def.param_key] : undefined;
  const complete = def ? fpDowntimeEngineValue(value) !== "" : false;
  const isBase = scenario?.role === "base";

  const dtRef = useRef({ changed: 0, total: 0 });
  dtRef.current = { changed: complete ? 1 : 0, total: def ? 1 : 0 };   // 렌더 중 대입 — 자식 effect 가 읽는다
  const upRef = useRef(rest?.onStats);
  upRef.current = rest?.onStats;
  const sharedRef = useRef(null);
  const onStats = useRef((s) => {
    sharedRef.current = s;
    const d = dtRef.current;
    upRef.current?.({ changed: (s?.changed || 0) + d.changed, total: (s?.total || 0) + d.total });
  }).current;

  useEffect(() => {
    if (isBase || !sharedRef.current) return;       // base 는 잠금 목록 · 공용 보고 전에는 합칠 것이 없다
    onStats(sharedRef.current);
  }, [complete, def, isBase]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    shared,
    onStats,
    key: `${scenario?.role || ""}|${scenario?.scenario_cd || ""}|${scenario?.__resetAt ?? ""}`,
    props: {
      def, value, planPeriod,
      accent: rest?.accent, disabled: rest?.disabled, view: rest?.view,
      onChange: (v) => { if (def) rest?.onParamChange?.(def.param_key, v); },
    },
  };
}

const ENTRY_SX = (accent) => ({
  width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.4,
  border: `1px dashed ${accent}70`, borderRadius: PARAM_CARD_RADIUS, bgcolor: "transparent", color: accent,
  fontFamily: "inherit", fontSize: FONT.CAPTION, fontWeight: 700, py: "5px", cursor: "pointer",
  "&:hover": { bgcolor: `${accent}0E`, borderColor: accent },
});

function DayPicker({ value, onPick, error, label, minDate, maxDate, disabled }) {
  return (
    <DatePicker format="yyyy-MM-dd" value={value} onChange={onPick} disabled={disabled}
      minDate={minDate || undefined} maxDate={maxDate || undefined}
      slotProps={{ textField: { size: "small", ...(error ? { error: true } : {}), inputProps: { "aria-label": label },
                                sx: { width: 150 } } }} />
  );
}

/** 「비조업 설정」 블록 — 행 = 시작일 ~ 종료일(포함). 대상 설비는 아래 「적용 대상」 블록에서 고른다. */
export function FpDowntimeFields({ def, value, onChange, accent = C.acc, disabled = false, view = "all", planPeriod = null }) {
  const { t } = useTranslation();
  const [pinned, setPinned] = useState(false);
  const rows = fpDowntimeRows(value);
  const rowsKey = JSON.stringify(rows);
  // 🔴 DatePicker 에 넘기는 Date 는 **참조를 고정**한다 — MUI X(6.x)는 value 참조가 바뀌면 입력 중인 값을
  //    바깥 값으로 되돌린다(부모가 다른 이유로 다시 그려질 때 치던 날짜가 지워진다).
  const dates = useMemo(
    () => rows.map((r) => ({ s: fpDtStrToDate(r.start), e: fpDtStrToDate(fpDowntimeLastDay(r.end)) })),
    [rowsKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  if (!def) return null;
  if (disabled && !rows.length) return null;
  const label = t(def.label_key || def.label || def.param_key);

  const write = (next) => {
    if (!next.length) { setPinned(false); onChange?.(""); return; }
    onChange?.(next);
  };
  if (view === "changed" && !rows.length && !pinned) {
    return (
      <Box data-testid="fp-downtime" sx={{ mt: 0.6 }}>
        <Box component="button" type="button" data-testid="fp-dt-entry" sx={ENTRY_SX(accent)}
             onClick={() => { setPinned(true); write([{ start: "", end: "" }]); }}>
          <AddIcon sx={{ fontSize: 14 }} />{label}
        </Box>
      </Box>
    );
  }

  const setRow = (i, patch) => write(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const pickStart = (i, dt) => {
    const s = fpDtDateToStr(dt);
    if (s === undefined) return;                        // 입력 중(Invalid Date) — 쓰지 않는다
    setRow(i, { start: s, end: rows[i].end || (s ? fpDowntimeExcl(s) : "") });   // 종료일이 비면 같은 날(하루 정지)
  };
  const pickEnd = (i, dt) => {
    const e = fpDtDateToStr(dt);
    if (e === undefined) return;
    setRow(i, { end: e ? fpDowntimeExcl(e) : "" });     // 화면은 포함 · 저장은 다음 날 0시
  };
  const outOfPlan = fpDowntimeOutOfPlan(rows, planPeriod);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box data-testid="fp-downtime"
           sx={{ mt: 0.8, p: "8px 10px", borderRadius: PARAM_CARD_RADIUS,
                 border: `0.5px solid ${C.border}`, bgcolor: C.bg2 }}>
        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1 }}>{label}</Typography>
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, mt: 0.2, mb: 0.6 }}>
          {t("fpScnResourceDowntimeHint")}
        </Typography>
        <Stack spacing={0.6}>
          {rows.map((r, i) => {
            const issue = fpDowntimeRowIssue(r);
            return (
              <Box key={i} data-testid="fp-dt-row">
                <Stack direction="row" alignItems="center" sx={{ gap: 0.6, flexWrap: "wrap" }}>
                  <DayPicker value={dates[i]?.s ?? null} onPick={(dt) => pickStart(i, dt)} disabled={disabled}
                             error={issue === "reversed"} label={t("fpScnResourceDowntimeStart")}
                             maxDate={dates[i]?.e} />
                  <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>~</Typography>
                  <DayPicker value={dates[i]?.e ?? null} onPick={(dt) => pickEnd(i, dt)} disabled={disabled}
                             error={issue === "reversed"} label={t("fpScnResourceDowntimeEnd")}
                             minDate={dates[i]?.s} />
                  {!disabled && (
                    // AiDeleteButton 은 ref·props 를 넘기지 않는다 — Tooltip 이 붙을 자리로 span 을 둔다.
                    <Tooltip title={t("delete")}>
                      <Box component="span" data-testid="fp-dt-del" sx={{ display: "inline-flex" }}>
                        <AiDeleteButton onClick={() => write(rows.filter((_, k) => k !== i))} />
                      </Box>
                    </Tooltip>
                  )}
                </Stack>
                {issue && (
                  <Typography data-testid="fp-dt-issue"
                              sx={{ fontSize: FONT.CAPTION, mt: 0.3, color: issue === "reversed" ? C.g3 : C.text3 }}>
                    {t(issue === "reversed" ? "fpScnResourceDowntimeReversed" : "fpScnResourceDowntimeIncomplete")}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
        {!disabled && (
          <AiButton variant="soft" size="sm" accent={accent} data-testid="fp-dt-add" sx={{ mt: 0.8 }}
                    onClick={() => write([...rows, { start: "", end: "" }])}>
            <AddIcon sx={{ fontSize: 12, mr: 0.3 }} />{t("addSegment")}
          </AiButton>
        )}
        {outOfPlan && (
          <Typography data-testid="fp-dt-out-of-plan" sx={{ fontSize: FONT.CAPTION, mt: 0.6, color: C.g2t }}>
            {t("fpScnResourceDowntimeOutOfPlan", { start: planPeriod.start, end: planPeriod.end })}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
}
